/**
 * Segment and filter types for the Segments API.
 * Reuses creator-crm filter types (MultiPlatformFilter, HandleFilter) with type discriminators.
 * Response data is transformed to camelCase by the client interceptor;
 * request body must be sent in snake_case (converted in the fetcher).
 */
import type {
  HandleFilter,
  MultiPlatformFilter,
} from '@/types/models/creator-crm/creatorCrm';

/** Source of truth for segment scope: API/compare use the values (user, brand, organization). */
export const SEGMENT_SCOPE = {
  USER: 'user',
  BRAND: 'brand',
  ORGANIZATION: 'organization',
} as const;

export type SegmentScope = (typeof SEGMENT_SCOPE)[keyof typeof SEGMENT_SCOPE];

/** Ordered list for UI (User → Brand → Organization). */
export const SEGMENT_SCOPE_ORDER: SegmentScope[] = [
  SEGMENT_SCOPE.USER,
  SEGMENT_SCOPE.BRAND,
  SEGMENT_SCOPE.ORGANIZATION,
];

/** Short scope labels for compact UI (dropdowns, chips). */
export const SEGMENT_SCOPE_SHORT_LABEL: Record<SegmentScope, string> = {
  [SEGMENT_SCOPE.USER]: 'Only me',
  [SEGMENT_SCOPE.BRAND]: 'Team',
  [SEGMENT_SCOPE.ORGANIZATION]: 'Org',
};

/** Full scope labels for visibility options (modals, radio groups). */
export const SEGMENT_SCOPE_VISIBILITY_LABEL: Record<SegmentScope, string> = {
  [SEGMENT_SCOPE.USER]: 'Only me',
  [SEGMENT_SCOPE.BRAND]: 'My team',
  [SEGMENT_SCOPE.ORGANIZATION]: 'Entire organization',
};

/** MultiPlatformFilter as returned/sent for segments (with type discriminator). */
export type SegmentMultiPlatformFilter = MultiPlatformFilter & {
  type: 'MultiPlatformFilter';
};

/** HandleFilter as returned/sent for segments (with type discriminator). */
export type SegmentHandleFilter = HandleFilter & {
  type: 'HandleFilter';
};

/** Union of segment filter types (with type discriminator). */
export type SegmentFilters = SegmentMultiPlatformFilter | SegmentHandleFilter;

/** Segment source: saved (from API) or virtual campaign segment. */
export type SegmentSource = 'saved' | 'campaign';

/** Segment as returned from the API (response is transformed to camelCase). */
export interface Segment {
  id: string;
  name: string;
  filters: SegmentFilters;
  createdAt: string;
  updatedAt: string;
  createdByDashboardUserId: string;
  agencyId: string;
  merchantId: string | null;
  scope: SegmentScope;
  /** Present for virtual campaign segments. */
  source?: SegmentSource;
  /** Present when source === 'campaign'; id of the campaign. */
  campaignId?: string;
}

/** Virtual segment derived from a campaign (shown in segments popper). */
export interface CampaignSegment extends Segment {
  source: 'campaign';
  campaignId: string;
}

export function isCampaignSegment(
  segment: Segment
): segment is CampaignSegment {
  return segment.source === 'campaign' && !!segment.campaignId;
}

/** Payload for creating a segment (camelCase in app; fetcher converts to snake_case). */
export interface CreateSegmentRequest {
  name: string;
  merchantId: string | null;
  scope?: SegmentScope;
  filters: SegmentFilters;
}

/** Payload for updating a segment (camelCase in app; fetcher converts to snake_case). */
export interface UpdateSegmentRequest {
  name: string;
  merchantId: string | null;
  scope?: SegmentScope;
  filters: SegmentFilters;
}
